import { useState, useEffect } from 'react'
import { useI18n } from '../i18n'
import { Shield, X, ChevronRight } from 'lucide-react'

/**
 * DiscoveryToast — slide-down NUDGE for Stedi insurance discovery results.
 *
 * v242.7.0 (D): reduced from a full inline picker to a lightweight nudge.
 * The rich pick-list + COB now live in InsuranceCandidateSheet, which this
 * nudge opens via onReview. The nudge only announces "coverage found" and
 * hands off — it no longer applies candidates itself.
 *
 * Mounts on HomeScreen. Renders only when:
 *   profile.insurance.discovery_result.acknowledged === false
 *   AND status is 'found' or 'multiple' AND candidates.length > 0
 *
 * Props:
 *   discoveryResult — profiles.insurance.discovery_result
 *   onReview        — () => void   open the full sheet
 *   onDismiss       — () => void   mark acknowledged without applying
 */

const NAVY  = '#15354F'
const BLUE  = '#46A8F9'
const LIGHT = '#A7D9FC'
const ICE   = '#E9F8FF'
const SLATE = '#94A3B8'

export default function DiscoveryToast({ discoveryResult, onReview, onDismiss }) {
  const { t } = useI18n()
  const [visible, setVisible] = useState(false)
  const [exiting, setExiting] = useState(false)

  useEffect(() => {
    if (!discoveryResult) return
    const t = setTimeout(() => setVisible(true), 80)
    return () => clearTimeout(t)
  }, [discoveryResult])

  if (!discoveryResult) return null
  const status = discoveryResult.status
  const candidates = discoveryResult.candidates || []
  if (status !== 'found' && status !== 'multiple') return null
  if (candidates.length === 0) return null
  if (discoveryResult.acknowledged) return null

  const single = candidates.length === 1
  const top = candidates[0]
  const headline = single
    ? `We found a policy with ${top?.payerName || 'an insurer'}.`
    : `We found ${candidates.length} possible plans for you.`

  const dismiss = () => {
    setExiting(true)
    setTimeout(() => { onDismiss?.() }, 220)
  }
  const review = () => {
    setExiting(true)
    setTimeout(() => { onReview?.() }, 200)
  }

  return (
    <div
      className="fixed left-2 right-2 z-50 pointer-events-none"
      style={{ top: 'max(env(safe-area-inset-top, 0px), 8px)' }}
    >
      <div
        className="pointer-events-auto mx-auto"
        style={{
          maxWidth: 440,
          transform: exiting ? 'translateY(-120%)' : (visible ? 'translateY(0)' : 'translateY(-120%)'),
          opacity: exiting ? 0 : (visible ? 1 : 0),
          transition: 'transform 240ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 200ms ease',
        }}
      >
        <div
          className="rounded-2xl overflow-hidden flex items-center gap-3 p-3.5"
          style={{ background: '#FFFFFF', border: `1.5px solid ${LIGHT}`, boxShadow: '0 12px 32px rgba(21,53,79,0.18)' }}
        >
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
               style={{ background: ICE, border: `1.5px solid ${LIGHT}` }}>
            <Shield size={18} style={{ color: BLUE }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: BLUE }}>
              {t('insuranceCandidates.coverageFound', 'Coverage found')}
            </p>
            <p className="text-[12.5px] font-semibold leading-snug mt-0.5" style={{ color: NAVY }}>
              {headline}
            </p>
          </div>
          <button
            onClick={review}
            className="flex-shrink-0 inline-flex items-center gap-1 text-[11.5px] font-bold px-3 py-2 rounded-full"
            style={{ background: BLUE, color: '#FFFFFF' }}
          >
            {t('describe.review')} <ChevronRight size={14} />
          </button>
          <button onClick={dismiss} className="p-1.5 -m-1.5 rounded-full flex-shrink-0" style={{ color: SLATE }}>
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  )
}
